import queryString from 'query-string'

export const ENTRY_TYPES = Object.freeze({ LOGIN: 'login', SIGNUP: 'signup' })

const SIGNUP_ALIASES = ['signup', 'sign-up', 'register']

const first = (value) => (Array.isArray(value) ? value[0] : value)

// A Mongo ObjectId. Anything else in renewalId is ignored.
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i

/**
 * The only places a `next` param may send someone, after they have signed in.
 * Keys are the values the backend puts in links; each maps an id to an
 * in-app (hash router) path. Anything not listed here is ignored, so a crafted
 * link cannot redirect off the app or to an arbitrary route.
 */
export const NEXT_DESTINATIONS = Object.freeze({
    'addon-renewal': (renewalId) => '/addon-renewal/' + renewalId,
    // The member's own renewal (renewal reminder emails, templates 1 and 2):
    // the account page with the renew dialog open. Takes no renewalId.
    'renew': () => '/renew',
    // The account page, nothing to open. A renewal reminder's signup link
    // (template 2) uses it for a self-pay add-on whose primary has not renewed
    // yet: the link must still carry a destination, because without one the
    // signup form is the new-member purchase flow, not "link my record".
    'account': () => '/'
})

// Destinations that take no renewalId.
const NO_ID_DESTINATIONS = ['renew', 'account']

/**
 * Reads the entry-point query params carried by admin-generated links, e.g.
 *   /#/?type=signup&email=jane%40example.com
 *   /#/?token=<loginToken>
 *
 *   email  prefills (and locks) the email field on the login / signup form
 *   type   "signup" (or "register") opens the admin-managed signup form,
 *          anything else (or absent) opens the member login form
 *   token  a login token carried by a one-click link in an email. Present
 *          means "try to sign this person in without the login form".
 *   next, renewalId
 *          where to continue once signed in, e.g.
 *          ?next=addon-renewal&renewalId=<ObjectId> -> /addon-renewal/<id>,
 *          ?next=renew -> /renew (the renew dialog; no renewalId),
 *          ?next=account -> / (the account page; no renewalId).
 *          Both are dropped (returned as '') unless next is in
 *          NEXT_DESTINATIONS and, where it needs one, renewalId looks like an
 *          ObjectId.
 *
 * Note: encode the email (a raw "+" is parsed as a space).
 */
export const getEntryParams = (search = '') => {
    const qs = queryString.parse(search || '')
    const rawEmail = first(qs.email)
    const rawType = first(qs.type)
    const rawToken = first(qs.token)

    const email = typeof rawEmail === 'string' ? rawEmail.trim() : ''
    const normalizedType = typeof rawType === 'string' ? rawType.trim().toLowerCase() : ''
    const type = SIGNUP_ALIASES.includes(normalizedType) ? ENTRY_TYPES.SIGNUP : ENTRY_TYPES.LOGIN
    const token = typeof rawToken === 'string' ? rawToken.trim() : ''

    const rawNext = first(qs.next)
    const rawRenewalId = first(qs.renewalId)
    const nextKey = typeof rawNext === 'string' ? rawNext.trim().toLowerCase() : ''
    const renewalIdCandidate = typeof rawRenewalId === 'string' ? rawRenewalId.trim() : ''
    const takesNoId = NO_ID_DESTINATIONS.includes(nextKey)
    const allowed = Object.prototype.hasOwnProperty.call(NEXT_DESTINATIONS, nextKey) &&
        (takesNoId || OBJECT_ID_RE.test(renewalIdCandidate))
    const next = allowed ? nextKey : ''
    const renewalId = allowed && !takesNoId ? renewalIdCandidate : ''

    return {
        email,
        type,
        token,
        // Set by protectedRoute when this link's sign-in token did not work
        // (expired, already used, or replaced by a newer email).
        linkExpired: first(qs.link) === 'expired',
        next,
        renewalId,
        hasEmail: email.length > 0,
        hasToken: token.length > 0,
        hasNext: allowed
    }
}

/**
 * The in-app path to continue to after authentication, or null. Takes a query
 * string or the object getEntryParams returns.
 */
export const getNextPath = (searchOrParams = '') => {
    const params = typeof searchOrParams === 'object' && searchOrParams !== null
        ? searchOrParams
        : getEntryParams(searchOrParams)
    if (!params.hasNext) { return null }
    return NEXT_DESTINATIONS[params.next](params.renewalId)
}

/**
 * The same query string with `token` removed, so the caller can replace the
 * URL once the token has been used. Everything else (email, next, renewalId)
 * is kept, so a dead token still leaves the prefilled login and the intent.
 *
 * A login token in the address bar is a standing hazard: it stays in history,
 * is copied when somebody shares the link, and rides along in the Referer
 * header of later requests. It is wanted exactly once, on arrival.
 *
 * { expired: true } also marks the link as expired (link=expired), so the
 * login form can say why the member is not signed in.
 */
export const stripTokenFromSearch = (search = '', { expired = false } = {}) => {
    const qs = queryString.parse(search || '')
    delete qs.token
    if (expired) { qs.link = 'expired' }
    const rest = queryString.stringify(qs)
    return rest ? '?' + rest : ''
}
